import { Address, beginCell, storeStateInit } from "@ton/core";
import { mnemonicToPrivateKey, sha256_sync as sha256Sync } from "@ton/crypto";
import axios from "axios";
import nacl from "tweetnacl";

import { getWalletContract } from "@arc/sdk";

import { type DAppManifest, type DeviceInfo, type TonAddressItemReply } from "./types";

export function concatUint8Arrays(buffer1: Uint8Array, buffer2: Uint8Array): Uint8Array {
  const mergedArray = new Uint8Array(buffer1.length + buffer2.length);
  mergedArray.set(buffer1);
  mergedArray.set(buffer2, buffer1.length);
  return mergedArray;
}

export function splitToUint8Arrays(array: Uint8Array, index: number): [Uint8Array, Uint8Array] {
  if (index >= array.length) {
    throw new Error("Index is out of buffer");
  }

  const subArray1 = array.slice(0, index);
  const subArray2 = array.slice(index);
  return [subArray1, subArray2];
}

export function toHexString(byteArray: Uint8Array): string {
  let hexString = "";
  byteArray.forEach((byte) => {
    // eslint-disable-next-line no-bitwise
    hexString += ("0" + (byte & 0xff).toString(16)).slice(-2);
  });
  return hexString;
}
export function hexToByteArray(hexString: string): Uint8Array {
  if (hexString.length % 2 !== 0) {
    throw new Error(`Cannot convert ${hexString} to bytesArray`);
  }
  const result = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    result[i / 2] = parseInt(hexString.slice(i, i + 2), 16);
  }
  return result;
}

export const getMaxMessages = (walletVersion: "V5R1" | "V4") => (walletVersion === "V5R1" ? 255 : 4);

export const getPlatform = (): DeviceInfo["platform"] => {
  const platform =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any)?.userAgentData?.platform || window.navigator.platform;

  const { userAgent } = window.navigator;

  const macosPlatforms = ["macOS", "Macintosh", "MacIntel", "MacPPC", "Mac68K"];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iphonePlatforms = ["iPhone"];
  const iosPlatforms = ["iPad", "iPod"];

  let os: DeviceInfo["platform"] | null = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "mac";
  } else if (iphonePlatforms.indexOf(platform) !== -1) {
    os = "iphone";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "ipad";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "windows";
  } else if (/Android/.test(userAgent)) {
    os = "android";
  } else if (/Linux/.test(platform)) {
    os = "linux";
  }

  return os!;
};

export const toTonAddressItemReply = async (
  wallet: {
    publicKey: string;
    walletVersion: "V5R1" | "V4";
    address: string;
  },
  network: "testnet" | "mainnet",
  mnemonic: string[],
): Promise<TonAddressItemReply> => {
  const keyPair = await mnemonicToPrivateKey(mnemonic);

  const WalletContract = getWalletContract(wallet.walletVersion);
  const contract = WalletContract.create({
    workchain: 0,
    publicKey: keyPair.publicKey,
  });

  return {
    name: "ton_addr",
    address: contract.address.toRawString(),
    network: network === "mainnet" ? "-239" : "-3",
    walletStateInit: beginCell().storeWritable(storeStateInit(contract.init)).endCell().toBoc().toString("base64"),
    publicKey: wallet.publicKey,
  };
};

export interface ConnectProofPayload {
  timestamp: number;
  bufferToSign: Buffer;
  domainBuffer: Buffer;
  payload: string;
  origin: string;
  messageBuffer: Buffer;
}

export const tonConnectProofPayload = (
  timestamp: number,
  origin: string,
  wallet: string,
  payload: string,
): ConnectProofPayload => {
  const timestampBuffer = Buffer.allocUnsafe(8);
  timestampBuffer.writeBigInt64LE(BigInt(timestamp));

  const domainBuffer = Buffer.from(new URL(origin).host);

  const domainLengthBuffer = Buffer.allocUnsafe(4);
  domainLengthBuffer.writeInt32LE(domainBuffer.byteLength);

  const address = Address.parse(wallet);

  const addressWorkchainBuffer = Buffer.allocUnsafe(4);
  addressWorkchainBuffer.writeInt32BE(address.workChain);

  const addressBuffer = Buffer.concat([addressWorkchainBuffer, address.hash]);

  const messageBuffer = Buffer.concat([
    Buffer.from("ton-proof-item-v2/", "utf8"),
    addressBuffer,
    domainLengthBuffer,
    domainBuffer,
    timestampBuffer,
    Buffer.from(payload),
  ]);

  const bufferToSign = Buffer.concat([
    Buffer.from("ffff", "hex"),
    Buffer.from("ton-connect", "utf8"),
    Buffer.from(sha256Sync(messageBuffer)),
  ]);

  return {
    timestamp,
    bufferToSign,
    domainBuffer,
    payload,
    origin,
    messageBuffer,
  };
};

export const signTonConnectOver = (mnemonic: string[]) => async (bufferToSign: Buffer) => {
  const keyPair = await mnemonicToPrivateKey(mnemonic);

  return nacl.sign.detached(Buffer.from(sha256Sync(bufferToSign)), keyPair.secretKey);
};

export const getManifestResponse = async (manifestUrl: string) => {
  try {
    const { data: manifest } = await axios.get<DAppManifest>(manifestUrl);

    return manifest;
  } catch {
    // NOTE: Request file with CORS header;
    const { data: manifest } = await axios.get<DAppManifest>(`https://corsproxy.architecton.site/${manifestUrl}`);

    return manifest;
  }
};
